import { OutOfOfficeTabs } from "@clockwise/web-commons/src/components/calendar";
import { EventType } from "@clockwise/web-commons/src/util/ActiveEventContext";
import { useReadCalendarColors } from "@clockwise/web-commons/src/util/CalendarColorsContext";
import { getOwnCalendarColor } from "@clockwise/web-commons/src/util/calendar-coloring";
import { DateTime, Interval } from "luxon";
import React from "react";
import { usePlannerContext } from "../Context";
import { hasIntervalAllDayLong } from "./utils/hasIntervalAllDayLong";

type Props = {
  dateTimes: DateTime[];
  onClick: (calendarId: string, externalEventId: string, type: EventType) => void;
};

export const AllDayEventsUnderlay = ({ dateTimes, onClick }: Props) => {
  const { allDayOOOEvents: allDayOOOEventsNullable } = usePlannerContext();
  const calendarColorSets = useReadCalendarColors();

  const allDayOOOEventsByDay = allDayOOOEventsNullable ?? {};
  const oooEventsFlat = Object.values(allDayOOOEventsByDay).flatMap((events) => events);
  const outOfOfficeEvents = oooEventsFlat.filter((event) =>
    dateTimes.some(
      (dateTime) =>
        event.interval.contains(dateTime) &&
        (event.interval.toDuration().as("days") >= 1 || hasIntervalAllDayLong(event)),
    ),
  );

  if (!outOfOfficeEvents.length) {
    return null;
  }

  return (
    <div className="cw-h-6 cw-w-full cw-relative cw-flex cw-flex-row cw-flex-1">
      <div className="cw-w-[42px] cw-overflow-hidden" />
      <div className="cw-h-full cw-w-full cw-relative cw-overflow-y-hidden [scrollbar-gutter:stable]">
        <OutOfOfficeTabs
          dateTimes={dateTimes}
          outOfOfficeIntervals={outOfOfficeEvents.map((event) => ({
            colorSet: calendarColorSets[event.calendarIds[0]] || getOwnCalendarColor(),
            // passing in full day is a bit of kludge, but will be required unless we update positioner logic
            interval: Interval.fromDateTimes(
              event.interval.start.startOf("day"),
              event.interval.start.endOf("day"),
            ),
            key: event.key,
            onClick: () =>
              onClick(event.calendarIds[0], event.externalEventId || "", EventType.Event),
            subText: event.subText,
            text: event.text,
          }))}
        />
      </div>
    </div>
  );
};
